import { ReactNode, createContext, useContext, useState } from 'react';

import {
  createUseFeatureFlag,
  EPartner,
  EEnvironment,
  EStatus,
  TFeatureFlagApiResponse,
} from '@sureapp/platform-sdk';

const { REACT_APP_TENANT, REACT_APP_ENV } = process.env;

const FRESHNESS_DURATION_MS = 300000; // 1000ms * 60s * 5min

const LOCAL_STORAGE_FEATURE_FLAG_KEY = 'APP_CONFIG_FEATURE_FLAG';
const LOCAL_STORAGE_EXPIRE_TIME_KEY = 'APP_CONFIG_EXPIRE_TIME';

const environment = ['development', 'test'].includes(REACT_APP_ENV)
  ? 'qa'
  : REACT_APP_ENV;

export type TFeatureFlagContext = {
  flags: TFeatureFlagApiResponse | null;
  setFlags: (value: TFeatureFlagApiResponse) => void;
  status: EStatus;
  setStatus: (value: EStatus) => void;
};

function hasCacheExpired() {
  const now = Date.now();
  const timeCached = localStorage.getItem(LOCAL_STORAGE_EXPIRE_TIME_KEY) || '0';

  const isLongerThanExpirationTime =
    now - Number(timeCached) > FRESHNESS_DURATION_MS;
  return isLongerThanExpirationTime;
}

export const FeatureFlagContext = createContext<TFeatureFlagContext>({
  flags: null,
  setFlags: () => {},
  status: 'PENDING',
  setStatus: () => {},
});

const useAppConfigFeatureFlags = createUseFeatureFlag({
  partner: REACT_APP_TENANT as EPartner,
  environment: environment as EEnvironment,
  division: 'berry',
  vertical: 'auto',
});

const useFeatureFlags = (flagsToCheck?: string[]) => {
  const { flags, setFlags, status, setStatus } = useContext(FeatureFlagContext);
  return useAppConfigFeatureFlags({
    flags,
    setFlags,
    status,
    setStatus,
    flagsToCheck,
  });
};

export function FeatureFlagContextProvider({
  children,
}: {
  children: ReactNode;
}) {
  const cachedFlags = localStorage.getItem(LOCAL_STORAGE_FEATURE_FLAG_KEY);

  const shouldUseCachedFlags = cachedFlags && !hasCacheExpired();

  const [flags, setFlags] = useState<TFeatureFlagContext['flags']>(
    shouldUseCachedFlags ? JSON.parse(cachedFlags) : null
  );
  const [status, setStatus] = useState<TFeatureFlagContext['status']>(
    'PENDING'
  );

  function setAndStoreFeatureFlags(featureFlags: TFeatureFlagApiResponse) {
    const now = Date.now().toString();
    localStorage.setItem(
      LOCAL_STORAGE_FEATURE_FLAG_KEY,
      JSON.stringify(featureFlags)
    );
    localStorage.setItem(LOCAL_STORAGE_EXPIRE_TIME_KEY, now);

    setFlags(featureFlags);
  }
  return (
    <FeatureFlagContext.Provider
      value={{ flags, setFlags: setAndStoreFeatureFlags, status, setStatus }}
    >
      {children}
    </FeatureFlagContext.Provider>
  );
}

export default useFeatureFlags;
